import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { errorText, useConfirm, useToast } from '@despachador/ui';
import { api, socket, useRealtime, useSession } from './session';

/**
 * Silent alerts of the drivers (PNC-001, docs/panic.md), for the whole panel: whoever may see them
 * (panic.view) gets them on any screen. An unattended alert sounds in a loop and makes the tab title
 * blink until somebody presses «La atiendo»; the strip under the header and En vivo show it first.
 */

export type PanicStatus = 'ACTIVE' | 'ACKNOWLEDGED' | 'CLOSED' | 'CANCELLED';
export type PanicOutcome = 'SAFE' | 'POLICE' | 'FALSE_ALARM' | 'NO_CONTACT' | 'OTHER';
export interface PanicAlert {
  id: string;
  status: PanicStatus;
  startedAt: string;
  driver: { id: string; code: string; name: string; phone: string | null };
  vehicle: { id: string; plate: string; make: string | null; model: string | null; color: string | null } | null;
  service: { id: string; code: string; status: string; origin: string | null; destination: string | null; customerName: string | null; customerPhone: string | null } | null;
  position: { lat: number; lng: number; accuracyM: number | null; at: string } | null;
  acknowledged: { at: string; by: string; name: string | null } | null;
  closed: { at: string; by: string; name: string | null; outcome: PanicOutcome; note: string | null } | null;
  cancelledAt: string | null;
}
export interface PanicEvent {
  kind: 'RAISED' | 'NOTICE' | 'ACKNOWLEDGED' | 'TAKEN_OVER' | 'EMERGENCY_CALL' | 'CLOSED' | 'CANCELLED';
  channel: 'WHATSAPP' | 'WEB_PUSH' | null;
  delivery: 'SENT' | 'DELIVERED' | 'READ' | 'FAILED' | null;
  at: string;
  user: { id: string; name: string | null } | null;
}
export interface PanicDetail extends PanicAlert {
  points: Array<{ lat: number; lng: number; accuracyM: number | null; at: string }>;
  events: PanicEvent[];
}
export type TrackPoint = { lat: number; lng: number; at: string };

export const OUTCOME: Record<PanicOutcome, { label: string; tone: string }> = {
  SAFE: { label: 'A salvo, sin intervención', tone: 'ok' },
  POLICE: { label: 'Se llamó a la policía', tone: 'info' },
  FALSE_ALARM: { label: 'Falsa alarma', tone: '' },
  NO_CONTACT: { label: 'Sin contacto con el conductor', tone: 'warn' },
  OTHER: { label: 'Otro', tone: '' },
};

/** «0:42», «12:05», «1:02:10»: how long an alert has been open (mono, in the strip and the detail). */
export function elapsed(from: string, now: number, to?: string | null) {
  const s = Math.max(0, Math.floor(((to ? new Date(to).getTime() : now) - new Date(from).getTime()) / 1000));
  const h = Math.floor(s / 3600);
  const mm = String(Math.floor((s % 3600) / 60)).padStart(h ? 2 : 1, '0');
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** First name and the initial of the last one: «Laura M.». */
export const shortName = (name: string | null | undefined) => {
  const [first, second] = (name ?? '').split(/\s+/).filter(Boolean);
  return first ? `${first}${second ? ` ${second[0]}.` : ''}` : 'Alguien';
};

// ---------------------------------------------------------------- sound
/**
 * Its own tone, unlike any service sound: two high notes and a pause, on a loop. Browsers only let a
 * page play after a gesture, so the context is resumed on the first click or key of the session.
 */
class PanicTone {
  private ctx: AudioContext | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  constructor() {
    const unlock = () => void this.context()?.resume().catch(() => undefined);
    window.addEventListener('pointerdown', unlock, { capture: true });
    window.addEventListener('keydown', unlock, { capture: true });
  }
  private context() {
    try {
      this.ctx ??= new AudioContext();
    } catch {
      return null;
    }
    return this.ctx;
  }
  get blocked() {
    return !this.ctx || this.ctx.state !== 'running';
  }
  private beep(at: number, freq: number) {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(0.18, at + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.22);
    osc.connect(gain).connect(ctx.destination);
    osc.start(at);
    osc.stop(at + 0.25);
  }
  private phrase() {
    const ctx = this.context();
    if (!ctx || ctx.state !== 'running') return;
    const t = ctx.currentTime;
    this.beep(t, 1175);
    this.beep(t + 0.28, 1568);
    this.beep(t + 0.56, 1175);
    this.beep(t + 0.84, 1568);
  }
  start() {
    if (this.timer) return;
    void this.context()?.resume().catch(() => undefined);
    this.phrase();
    this.timer = setInterval(() => this.phrase(), 2200);
  }
  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }
}
let tone: PanicTone | null = null;

// ---------------------------------------------------------------- state
interface PanicCtx {
  enabled: boolean;
  /** open alerts: unattended first, then the oldest */
  open: PanicAlert[];
  /** cancelled by the driver in the last 30 s: the strip says so before letting go */
  cancelled: PanicAlert[];
  tracks: Record<string, TrackPoint[]>;
  sounding: boolean;
  audioBlocked: boolean;
  /** «La atiendo», asking first when somebody else already attends it; true when it was taken */
  acknowledge: (alert: PanicAlert) => Promise<boolean>;
  /** an alert changed in a detail (closed, 123 called): the shared state follows */
  upsert: (alert: PanicAlert) => void;
}
const Ctx = createContext<PanicCtx>({ enabled: false, open: [], cancelled: [], tracks: {}, sounding: false, audioBlocked: false, acknowledge: async () => false, upsert: () => undefined });
export const usePanicAlerts = () => useContext(Ctx);

const CANCELLED_SHOWN_MS = 30_000;
const order = (a: PanicAlert, b: PanicAlert) => Number(b.status === 'ACTIVE') - Number(a.status === 'ACTIVE') || new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime();

export function PanicProvider({ children }: { children: ReactNode }) {
  const { can, me } = useSession();
  const enabled = can('panic.view');
  return enabled ? <PanicState userId={me.user.id}>{children}</PanicState> : <>{children}</>;
}

function PanicState({ userId, children }: { userId: string; children: ReactNode }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [alerts, setAlerts] = useState<Record<string, PanicAlert>>({});
  const [tracks, setTracks] = useState<Record<string, TrackPoint[]>>({});
  const [audioBlocked, setAudioBlocked] = useState(false);
  const known = useRef(new Set<string>());

  const loadTrack = useCallback(async (id: string) => {
    try {
      const d = await api.get<PanicDetail>(`/admin/panic/${id}`);
      setTracks((t) => ({ ...t, [id]: d.points.map((p) => ({ lat: p.lat, lng: p.lng, at: p.at })) }));
    } catch {
      /* the live points still arrive */
    }
  }, []);

  const upsert = useCallback(
    (a: PanicAlert) => {
      setAlerts((all) => ({ ...all, [a.id]: a }));
      if (!known.current.has(a.id) && (a.status === 'ACTIVE' || a.status === 'ACKNOWLEDGED')) {
        known.current.add(a.id);
        void loadTrack(a.id);
      }
    },
    [loadTrack],
  );

  // what is open now: at start, on every reconnection (events may have been missed) and as a slow safety net
  const reload = useCallback(async () => {
    try {
      const r = await api.get<{ items: PanicAlert[] }>('/admin/panic?status=open&limit=50');
      setAlerts((all) => {
        const next: Record<string, PanicAlert> = {};
        // keep the recently cancelled ones for the strip; drop open ones that are no longer open
        for (const a of Object.values(all)) if (a.status === 'CANCELLED') next[a.id] = a;
        for (const a of r.items) next[a.id] = a;
        return next;
      });
      for (const a of r.items) {
        if (known.current.has(a.id)) continue;
        known.current.add(a.id);
        void loadTrack(a.id);
      }
    } catch {
      /* the next attempt */
    }
  }, [loadTrack]);
  useEffect(() => {
    void reload();
    const off = socket.on('auth.ok', () => void reload());
    const id = setInterval(() => void reload(), 60_000);
    return () => {
      off();
      clearInterval(id);
    };
  }, [reload]);

  useRealtime(
    'panic.raised',
    (m: { alert: PanicAlert }) => {
      upsert(m.alert);
      // the system notification stays until it is clicked (only when the browser already allows it)
      try {
        if ('Notification' in window && Notification.permission === 'granted' && document.visibilityState !== 'visible') {
          const n = new Notification('Alerta silenciosa', {
            body: `${m.alert.driver.name} · ${m.alert.vehicle?.plate ?? 'sin vehículo'}`,
            tag: `panic-${m.alert.id}`,
            requireInteraction: true,
          });
          n.onclick = () => {
            window.focus();
            window.location.assign(`/en-vivo?alerta=${m.alert.id}`);
          };
        }
      } catch {
        /* notifications not available */
      }
    },
    [upsert],
  );
  useRealtime('panic.updated', (m: { alert: PanicAlert }) => upsert(m.alert), [upsert]);
  useRealtime('panic.location', (m: { alertId: string; lat: number; lng: number; accuracyM: number | null; at: string }) => {
    setTracks((t) => ({ ...t, [m.alertId]: [...(t[m.alertId] ?? []), { lat: m.lat, lng: m.lng, at: m.at }] }));
    setAlerts((all) => {
      const a = all[m.alertId];
      return a ? { ...all, [a.id]: { ...a, position: { lat: m.lat, lng: m.lng, accuracyM: m.accuracyM, at: m.at } } } : all;
    });
  });

  const now = Date.now();
  const list = Object.values(alerts);
  const open = useMemo(() => list.filter((a) => a.status === 'ACTIVE' || a.status === 'ACKNOWLEDGED').sort(order), [alerts]); // eslint-disable-line react-hooks/exhaustive-deps
  const cancelled = list.filter((a) => a.status === 'CANCELLED' && a.cancelledAt && now - new Date(a.cancelledAt).getTime() < CANCELLED_SHOWN_MS);
  // the cancelled notice leaves the strip by itself
  useEffect(() => {
    if (!cancelled.length) return;
    const id = setTimeout(() => setAlerts((all) => ({ ...all })), 1000);
    return () => clearTimeout(id);
  });
  const unattended = open.some((a) => a.status === 'ACTIVE');

  // sound and a blinking tab title until somebody takes every alert
  useEffect(() => {
    tone ??= new PanicTone();
    if (!unattended) {
      tone.stop();
      return;
    }
    tone.start();
    const title = document.title;
    let on = false;
    const blink = setInterval(() => {
      on = !on;
      document.title = on ? '● Alerta silenciosa' : title;
      setAudioBlocked(tone!.blocked);
    }, 1000);
    return () => {
      tone?.stop();
      clearInterval(blink);
      document.title = title;
    };
  }, [unattended]);

  const acknowledge = useCallback(
    async (a: PanicAlert) => {
      const takeOver = a.status === 'ACKNOWLEDGED' && a.acknowledged?.by !== userId;
      if (takeOver) {
        const ok = await confirm({ title: 'Tomar la alerta', message: `La atiende ${a.acknowledged?.name ?? 'otra persona'}. ¿La tomas tú?`, confirmLabel: 'La tomo' });
        if (!ok) return false;
      }
      try {
        upsert(await api.post<PanicAlert>(`/admin/panic/${a.id}/acknowledge`, takeOver ? { takeOver: true } : {}));
        return true;
      } catch (err) {
        toast(errorText(err), true);
        void reload();
        return false;
      }
    },
    [confirm, reload, toast, upsert, userId],
  );

  const value = useMemo<PanicCtx>(
    () => ({ enabled: true, open, cancelled, tracks, sounding: unattended, audioBlocked: unattended && audioBlocked, acknowledge, upsert }),
    [open, cancelled, tracks, unattended, audioBlocked, acknowledge, upsert],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
